import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { createApiClient } from "@/api/client";
import { queryKeys } from "@/api/queryClient";
import type { Connector, ConnectorAuth, ConnectorConnectionStatus, AgentConnectorSummary } from "@/types";

function useClient() {
  const { session, logout } = useAuth();
  return () => createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
}

// #231: Connector-Eingaben (Create/Update). KEINE Secrets — die kommen via connect.
// unified-managed-mcp: ENV-Variablen-Deklaration eines lokalen Connectors.
export interface ConnectorEnvVarSpecInput {
  name: string;
  secret?: boolean;
  required?: boolean;
  label?: string | null;
  placeholder?: string | null;
}
export interface ConnectorLocalInput {
  command: string;
  args?: string[];
  envSchema?: ConnectorEnvVarSpecInput[];
}
// #249: managed CLI-Tool (transport=cli).
export interface ConnectorCliInput {
  bin: string;
  installCmd?: string | null;
  uninstallCmd?: string | null;
  authShape: string;
  binding?: string | null;
  extraEnv?: ConnectorEnvVarSpecInput[];
}
// #253: generischer REST-API-Connector (transport=openapi).
export interface ConnectorOpenApiInput {
  baseUrl: string;
  specUrl: string;
  mode?: string;
  includeTags?: string[];
}
export interface CreateConnectorInput {
  name: string;
  description?: string;
  url?: string;
  transport: string;
  // #253 (UC-A): Auth als discriminated union (none|static|oauth|login).
  auth: ConnectorAuth;
  local?: ConnectorLocalInput;
  cli?: ConnectorCliInput;
  openapi?: ConnectorOpenApiInput;
}
export type UpdateConnectorInput = Partial<CreateConnectorInput>;

export function useCreateConnector(): UseMutationResult<Connector, Error, CreateConnectorInput> {
  const qc = useQueryClient();
  const getClient = useClient();
  return useMutation({
    mutationFn: (input) => getClient().post<Connector>("/connectors", input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.resource("connectors") }),
  });
}

export function useUpdateConnector(): UseMutationResult<Connector, Error, { id: string; patch: UpdateConnectorInput }> {
  const qc = useQueryClient();
  const getClient = useClient();
  return useMutation({
    mutationFn: ({ id, patch }) => getClient().patch<Connector>(`/connectors/${id}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.resource("connectors") }),
  });
}

// #279/E-3: Fachverantwortlichen setzen, uebergeben oder (nur Admin) entfernen.
//
// Ohne diese Aktion war #279 in der Praxis nicht nutzbar: der ganze Antrags-Ablauf greift
// erst, WENN ein Connector einen Verantwortlichen hat — und den konnte man in der
// Oberflaeche nur ansehen, nicht setzen.
//
// Invalidiert zusaetzlich die Zugriffs-Antraege: mit dem Owner wechselt die Warteschlange
// (`listForApprover` folgt der AKTUELLEN Ownership), und ownerlos macht offene Antraege
// gegenstandslos.
export function useSetConnectorOwner(): UseMutationResult<Connector, Error, { id: string; userId: string | null }> {
  const qc = useQueryClient();
  const getClient = useClient();
  return useMutation({
    mutationFn: ({ id, userId }) => getClient().put<Connector>(`/connectors/${id}/owner`, { userId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.resource("connectors") });
      void qc.invalidateQueries({ queryKey: queryKeys.resource("connector-access-requests") });
    },
  });
}

export function useDeleteConnector(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  const getClient = useClient();
  return useMutation({
    mutationFn: (id) => getClient().delete<void>(`/connectors/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.resource("connectors") }),
  });
}

// #231 (UC-2): api-key verbinden (Wert write-only → Vault).
export function useConnectApiKey(): UseMutationResult<ConnectorConnectionStatus, Error, { id: string; value: string }> {
  const qc = useQueryClient();
  const getClient = useClient();
  return useMutation({
    mutationFn: ({ id, value }) => getClient().post<ConnectorConnectionStatus>(`/connectors/${id}/connect`, { value }),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.resource("connectors") }),
  });
}

// #253 (UC-A): Login-Endpoint-Connector verbinden (admin, platform-scope). user/pw write-only.
export function useConnectLogin(): UseMutationResult<ConnectorConnectionStatus, Error, { id: string; username: string; password: string }> {
  const qc = useQueryClient();
  const getClient = useClient();
  return useMutation({
    mutationFn: ({ id, username, password }) =>
      getClient().post<ConnectorConnectionStatus>(`/connectors/${id}/connect-login`, { username, password }),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.resource("connectors") }),
  });
}

// #336 (UC-1): Client-Credentials-Connector verbinden (admin, platform-scope). clientId +
// clientSecret write-only → Vault; den Access-Token holt der Master selbst.
export function useConnectClientCredentials(): UseMutationResult<
  ConnectorConnectionStatus,
  Error,
  { id: string; clientId: string; clientSecret: string }
> {
  const qc = useQueryClient();
  const getClient = useClient();
  return useMutation({
    mutationFn: ({ id, clientId, clientSecret }) =>
      getClient().post<ConnectorConnectionStatus>(`/connectors/${id}/connect-client-credentials`, { clientId, clientSecret }),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.resource("connectors") }),
  });
}

// unified-managed-mcp: ENV-Bündel eines lokalen Connectors verbinden (admin, platform-scope).
export function useConnectEnv(): UseMutationResult<ConnectorConnectionStatus, Error, { id: string; vars: Record<string, string> }> {
  const qc = useQueryClient();
  const getClient = useClient();
  return useMutation({
    mutationFn: ({ id, vars }) => getClient().post<ConnectorConnectionStatus>(`/connectors/${id}/connect-env`, { vars }),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.resource("connectors") }),
  });
}

// #231 (UC-2b): OAuth-Flow starten → authorizeUrl (Admin öffnet sie im Browser).
export function useStartOAuth(): UseMutationResult<{ authorizeUrl: string }, Error, string> {
  const getClient = useClient();
  return useMutation({
    mutationFn: (id) => getClient().post<{ authorizeUrl: string }>(`/connectors/${id}/oauth/start`),
  });
}

// #231: App-Secret eines vertraulichen OAuth-Clients setzen (write-only → Vault). Admin.
export function useSetOAuthClientSecret(): UseMutationResult<void, Error, { id: string; value: string }> {
  const qc = useQueryClient();
  const getClient = useClient();
  return useMutation({
    mutationFn: ({ id, value }) => getClient().put<void>(`/connectors/${id}/oauth/client-secret`, { value }),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.resource("connectors") }),
  });
}

export function useClearOAuthClientSecret(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  const getClient = useClient();
  return useMutation({
    mutationFn: (id) => getClient().delete<void>(`/connectors/${id}/oauth/client-secret`),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.resource("connectors") }),
  });
}

export function useDisconnectConnector(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  const getClient = useClient();
  return useMutation({
    mutationFn: (id) => getClient().delete<void>(`/connectors/${id}/connect`),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.resource("connectors") }),
  });
}

// #231 (UC-3): Connector für einen Agent aktivieren/deaktivieren. Invalidiert die
// Per-Agent-Liste UND den Katalog (Reverse-Lookup/Usage).
export function useSetAgentConnector(
  agentId: string,
): UseMutationResult<AgentConnectorSummary, Error, { connectorId: string; enabled: boolean }> {
  const qc = useQueryClient();
  const getClient = useClient();
  return useMutation({
    mutationFn: ({ connectorId, enabled }) =>
      getClient().put<AgentConnectorSummary>(`/agents/${agentId}/connectors/${connectorId}`, { enabled }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.subResource("agents", agentId, "connectors") });
      qc.invalidateQueries({ queryKey: queryKeys.resource("connectors") });
    },
  });
}

// #231 (UC-7): Per-Agent-Connect (Scope owner|agent) über die UC-6-Endpoints. Owner ODER
// Admin (assertAgentOwner). Invalidiert die Per-Agent-Liste (connected/connectedScope).
export type OwnerConnectScope = "owner" | "agent";

function useAgentConnInvalidate(agentId: string) {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: queryKeys.subResource("agents", agentId, "connectors") });
    qc.invalidateQueries({ queryKey: queryKeys.resource("connectors") });
  };
}

export function useAgentConnectApiKey(
  agentId: string,
): UseMutationResult<void, Error, { connectorId: string; value: string; scope: OwnerConnectScope }> {
  const getClient = useClient();
  const invalidate = useAgentConnInvalidate(agentId);
  return useMutation({
    mutationFn: ({ connectorId, value, scope }) =>
      getClient().post<void>(`/agents/${agentId}/connectors/${connectorId}/connect`, { value, scope }),
    onSuccess: invalidate,
  });
}

export function useAgentStartOAuth(
  agentId: string,
): UseMutationResult<{ authorizeUrl: string }, Error, { connectorId: string; scope: OwnerConnectScope }> {
  const getClient = useClient();
  return useMutation({
    mutationFn: ({ connectorId, scope }) =>
      getClient().post<{ authorizeUrl: string }>(`/agents/${agentId}/connectors/${connectorId}/oauth/start`, { scope }),
  });
}

// #253 (UC-A): Per-Agent Login-Endpoint-Connector verbinden (scope owner|agent).
export function useAgentConnectLogin(
  agentId: string,
): UseMutationResult<void, Error, { connectorId: string; username: string; password: string; scope: OwnerConnectScope }> {
  const getClient = useClient();
  const invalidate = useAgentConnInvalidate(agentId);
  return useMutation({
    mutationFn: ({ connectorId, username, password, scope }) =>
      getClient().post<void>(`/agents/${agentId}/connectors/${connectorId}/connect-login`, { username, password, scope }),
    onSuccess: invalidate,
  });
}

// #336 (UC-1): Per-Agent Client-Credentials verbinden (scope owner|agent). Write-only → Vault.
export function useAgentConnectClientCredentials(
  agentId: string,
): UseMutationResult<void, Error, { connectorId: string; clientId: string; clientSecret: string; scope: OwnerConnectScope }> {
  const getClient = useClient();
  const invalidate = useAgentConnInvalidate(agentId);
  return useMutation({
    mutationFn: ({ connectorId, clientId, clientSecret, scope }) =>
      getClient().post<void>(`/agents/${agentId}/connectors/${connectorId}/connect-client-credentials`, {
        clientId,
        clientSecret,
        scope,
      }),
    onSuccess: invalidate,
  });
}

// unified-managed-mcp: Per-Agent ENV-Bündel eines lokalen Connectors verbinden (scope owner|agent).
export function useAgentConnectEnv(
  agentId: string,
): UseMutationResult<void, Error, { connectorId: string; vars: Record<string, string>; scope: OwnerConnectScope }> {
  const getClient = useClient();
  const invalidate = useAgentConnInvalidate(agentId);
  return useMutation({
    mutationFn: ({ connectorId, vars, scope }) =>
      getClient().post<void>(`/agents/${agentId}/connectors/${connectorId}/connect-env`, { vars, scope }),
    onSuccess: invalidate,
  });
}

export function useAgentDisconnect(
  agentId: string,
): UseMutationResult<void, Error, { connectorId: string; scope: OwnerConnectScope }> {
  const getClient = useClient();
  const invalidate = useAgentConnInvalidate(agentId);
  return useMutation({
    mutationFn: ({ connectorId, scope }) =>
      getClient().delete<void>(`/agents/${agentId}/connectors/${connectorId}/connect?scope=${scope}`),
    onSuccess: invalidate,
  });
}
